import * as React from 'react';
import withStyles from 'react-jss';
import { StylesProps } from 'src/Library/Types';
import { useGenericState } from 'src/Library/UseGenericState';
import Button from 'antd/lib/button';
import Tabs from 'antd/lib/tabs';
import Drawer from 'antd/lib/drawer';
import Select from 'antd/lib/select';
import Checkbox from 'antd/lib/checkbox';
import Icon from 'antd/lib/icon';
import message from 'antd/lib/message';
import Row from 'antd/lib/row';
import Col from 'antd/lib/col';
import Tooltip from 'antd/lib/tooltip';
import { useDebounce } from 'src/hooks/useDebounce';
import { getPorts } from 'src/api/containerTracking';
import { isValidArray, clone, isValidString, sleep } from 'src/Library/Helper';
import { renderSelectDropDown } from 'src/Library/Common';
import { Ports } from 'src/components/common/Types/CommonTypes';
import { setGridPorts, getAllDataResetFilter, resetPortList } from 'src/actions/discoverRatesActions';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import { DragIcon } from 'src/components/common/icons/DragIcon';
import { SortableElement, SortableContainer } from 'react-sortable-hoc';
import arrayMove from 'array-move';
import { PortSettingsSideDrawerStyles } from './PortSettingsSideDrawer.styles';
import { tokens } from 'src/design-system';
import { GridPorts } from './TypesInDiscoverRates/DiscoverRatesCommon';
import classNames from 'classnames';
import { portLimit } from './DiscoverRateUtils';
import { saveGridSearchHistory } from 'src/api/discoverRates';
import { SearchFilters } from './TypesInDiscoverRates/SearchFilters';
import { PortModule } from 'src/Library/Constants';
const dragIconLightColor = require('src/assets/images/dragIconLightColor.png');

interface PortSettingsSideDrawerProps extends StylesProps<ReturnType<typeof PortSettingsSideDrawerStyles>> {
  onClose: (success?: any) => void;
  isVisible: boolean;
  setGridPorts: (val) => void;
  gridPorts: GridPorts;
  getAllDataResetFilter: (apiCall? : boolean) => void;
  searchFilters: SearchFilters;
  resetPortList: (val: {}) => void;
}

interface StateProps {
  tabKey: string;
  queryString: string;
  ports: Ports [];
  searchingPorts: boolean;
  gridPortsSelected: GridPorts;
  isLoading: boolean;
}

const PortSettingsSideDrawer = (props: PortSettingsSideDrawerProps) => {

  const {
    onClose,
    classes,
    isVisible,
    setGridPorts,
    gridPorts,
    getAllDataResetFilter,
    searchFilters,
    resetPortList,
  } = props;

  const [state, setState] = useGenericState<StateProps>({
    tabKey: 'POD',
    queryString: '',
    ports: [],
    searchingPorts: false,
    gridPortsSelected: gridPorts,
    isLoading: false,
  });

  const {
    tabKey,
    queryString,
    ports,
    searchingPorts,
    gridPortsSelected,
    isLoading,
  } = state;

  const debouncedSearchTerm = useDebounce(queryString, 300);

  React.useEffect(() => {
    handlePortSearch(debouncedSearchTerm);
  }, [debouncedSearchTerm, tabKey]);

  const handlePortSearch = async (queryString) => {
    setState({ searchingPorts: true });
    const response = await getPorts({
      queryString,
      type: ['DRY', 'WET'],
      countryCodeList: tabKey === 'POL' ? ['IND'] : [],
      searchByCountryName: true,
      countryCodeListNotIn: tabKey === 'POD' ? ['IND'] : [],
    });
    setState({
      ports: response.data?.map(el => ({
        id: JSON.stringify(el),
        ...el,
      })) || [],
      searchingPorts: false,
    })
  };

  const renderPortOptionsWithCheckbox = () => {
    return (
      ports?.map((port: Ports) => <Select.Option
        {...port}
        style={{ color: tokens.colors.textSecondary }}
        key={port.id}
      >
        <div className={classes.portDiv}>
          <div>
            <Checkbox
              checked={gridPortsSelected[tabKey] && gridPortsSelected[tabKey]?.indexOf(port.id) !== -1}
              key={port.id}
              style={{ marginRight: '10px' }}/>
              <span style={{ marginRight: '8px', fontWeight: 600 }}>{port.name}</span>
              <span style={{ fontSize: '10px', color: '#525252'}}>{port.code}</span>
              {port?.type === 'DRY' &&
                <span className={classes.dryPort}>ICD</span>
              }
          </div>
          <div>
            <span style={{ marginRight: '8px' }}>{port.countryCode}</span>
            <img src={port?.flagS3Link} style={{ maxWidth: '16px' }} />
          </div>
        </div>
      </Select.Option>) || []
    );
  }

  const handlePortSelect = (val) => {
    let portsSelected = isValidArray(gridPortsSelected[tabKey]) ? clone(gridPortsSelected[tabKey]) : [];
    const isValuePresent = portsSelected?.some(port => port === val[0]?.key);
    if (isValuePresent) {
      portsSelected = portsSelected?.filter(port => port !== val[0]?.key)
    } else {
      if (portsSelected?.length >= portLimit[tabKey]) {
        message.error(`Maximum ${portLimit[tabKey]} ${tabKey}s can be selected`);
        return;
      } else {
        portsSelected.push(val[0].key)
      }
    }
    const portList = portsSelected?.map(item => isValidString(item) ? JSON.parse(item) : item)
    setState({
      gridPortsSelected: {
        ...gridPortsSelected,
        [`${tabKey}`]: portsSelected,
        [`${tabKey}List`]: portList
      }
    });
  };

  const renderPortSelect = () => {
    return (
      <Select
        className={classes.select}
        allowClear
        showSearch
        labelInValue
        value={[]}
        mode="multiple"
        showArrow
        dropdownClassName={classes.dropdown}
        dropdownRender={menu => renderSelectDropDown(menu, searchingPorts)}
        onSearch={queryString => setState({ queryString })}
        onFocus={() => setState({ queryString: '' })}
        onChange={(val) => handlePortSelect(val)}
        placeholder={`Search by ${tabKey} Name / Country`}
        getPopupContainer={(trigger: any) => trigger.parentNode}
        suffixIcon={<Icon type="search" style={{ color: tokens.colors.primaryDark }} />}
      >
        {renderPortOptionsWithCheckbox()}
      </Select>
    );
  };

  const handleRemovePort = (item) => {
    const ports = gridPortsSelected[`${tabKey}List`]?.filter(port => port.dbId !== item.dbId);
    const portsSelected = gridPortsSelected[`${tabKey}`]?.filter(port => port !== JSON.stringify(item));
    setState({
      gridPortsSelected: {
        ...gridPortsSelected,
        [`${tabKey}`]: portsSelected,
        [`${tabKey}List`]: ports,
      }
    });
  }

  const onSortEnd = ({ oldIndex, newIndex }) => {
    setState({
      gridPortsSelected: {
        ...gridPortsSelected,
        [`${tabKey}List`]: arrayMove(gridPortsSelected[`${tabKey}List`], oldIndex, newIndex),
      }
    });
  }

  const renderSelectedPorts = () => {
    if (isValidArray(gridPortsSelected[`${tabKey}List`])) {
      return (
       <div>
          <Row className={classes.portTitle}>
            <Col span={3}>S.N.</Col>
            <Col span={12}>Port</Col>
            <Col span={8}>Country</Col>
            <Col span={1}></Col>
          </Row>
          <SortableList
            distance={1}
            items={gridPortsSelected[`${tabKey}List`]}
            onSortEnd={onSortEnd}
            helperClass="sortableHelper" />
       </div>
      )
    }
    return;
  };

  const renderTabData = () => {
    return (
      <React.Fragment>
        {renderPortSelect()}
        {renderSelectedPorts()}
      </React.Fragment>
    );
  };

  const renderPortTabs = () => {
    return (
      <Tabs defaultActiveKey="POD"
       className={classes.tabStyle}
        onChange={(activeKey) => {
          setState({
            tabKey: activeKey,
          });
        }}>
        <Tabs.TabPane tab={
          <span>POL {isValidArray(gridPortsSelected.POL) ?
            <span style={{ fontWeight: 'normal'}}>({gridPortsSelected.POL.length})</span> : ''}
            &nbsp;<span className={classes.portAxis}>Y-axis</span>
          </span>
        } key="POL">
          {renderTabData()}
        </Tabs.TabPane>
        <Tabs.TabPane tab={
          <span>POD {isValidArray(gridPortsSelected.POD) ?
            <span style={{ fontWeight: 'normal'}}>({gridPortsSelected.POD.length})</span> : ''}
            &nbsp;<span className={classes.portAxis}>X-axis</span>
          </span>
        } key="POD">
          {renderTabData()}
        </Tabs.TabPane>
      </Tabs>
    )
  }

  const handleConfirm = async () => {
    setState({ isLoading: true });
    await sleep(10);
    resetPortList({});
    getAllDataResetFilter(false);
    setGridPorts(gridPortsSelected);
    window.localStorage.setItem('searchedPorts', JSON.stringify(gridPortsSelected));
    saveGridSearchHistory({
      polIds: getPortIds(gridPortsSelected?.POLList),
      podIds: getPortIds(gridPortsSelected?.PODList),
      container: JSON.parse(searchFilters.container || '[]')?.key,
      cargo: JSON.parse(searchFilters.cargo || '[]')?.key,
      mode: 'FCL',
      view: searchFilters.view,
    });
    onClose(true);
  }

  const getPortIds = (portList) => {
    return portList.map(port => port.dbId);
  };

  const renderFooter = () => {
    return (
      <div className={classes.footer}>
        <Button
          className={classes.confirmButton}
          loading={isLoading}
          disabled={!isValidArray(gridPortsSelected?.POLList) || !isValidArray(gridPortsSelected?.PODList)}
          onClick={() => handleConfirm()}>Confirm</Button>
        <Button
          className={classes.cancelButton}
          onClick={() => onClose()}>Cancel</Button>
      </div>
    )
  }

  const getPortBackground = (index) => {
    if (index % 2 === 0) return classes.evenRowStyle;
    return classes.oddRowStyle;
  }

  const ListItem = (record: any) => {
    return (
      <div>
        <span>
          {gridPortsSelected[`${tabKey}List`]
            .filter(item => item.dbId === record.record.value.dbId)
            .map((item) => {
              let index;
              gridPortsSelected[`${tabKey}List`]?.some((port, i) => (
                port.dbId === item.dbId ? (index = i, true) : false
              ));
              return (
                <Row className={classNames(classes.portRow, getPortBackground(index + 1))}>
                  <Col span={3}>{index + 1}</Col>
                  <Col span={12} className={classes.flexRow}>
                    <DragIcon id="dragIconOnHover"/>
                    <img src={dragIconLightColor} style={{
                      height: '10px',
                      margin: '0 5px',
                    }} id="defaultDragIcon"></img>
                    <Tooltip
                      title={item.name}
                      overlayStyle={{ fontSize: '12px' }}
                      mouseEnterDelay={0.5}>
                      <span className={classes.overflowText} style={{ maxWidth: '55%' }}>{item.name}</span>
                    </Tooltip>
                    <span style={{ fontSize: '8px', marginLeft: '8px' }}>{item.code}</span>
                  </Col>
                  <Col span={8} className={classes.flexRow}>
                    <img src={item?.flagS3Link} style={{ width: '16px', height: '12px', marginRight: '2px' }} />
                    <Tooltip
                      title={item.countryName}
                      overlayStyle={{ fontSize: '12px' }}
                      mouseEnterDelay={0.5}>
                      <span className={classes.overflowText}>{item.countryName}</span>
                    </Tooltip>
                  </Col>
                  <Col span={1} onClick={() => handleRemovePort(item)} id="remove">
                    <Icon type="close" style={{ fontSize: '8px' }} />
                  </Col>
                </Row>
              )
            }
          )}
        </span>
      </div>
    );
  }

  const SortableItem = SortableElement((props) => <ListItem record={props} />);
  const SortableList = SortableContainer((props) => {
    return (
      <ul style={{ paddingLeft: 0, marginBottom: '85px' }}>
        {gridPortsSelected[`${tabKey}List`].map((value, index) => (
          <SortableItem key={`item-${index}`} index={index} value={value} />
        ))}
      </ul>
    );
  });

  return (
    <Drawer
    visible={isVisible}
    closable={true}
    onClose={() => onClose()}
    className={classes.drawer}
    width={380}
    title="Settings" >
      {renderPortTabs()}
      {renderFooter()}
    </Drawer>
  );
};

const mapStateToProps = ({ discoverRatesReducer }, ownProps) => ({
  gridPorts: discoverRatesReducer.gridPorts,
  searchFilters: discoverRatesReducer.searchFilters,
});

const mapDispatchToProps = (dispatch) => {
  return bindActionCreators({
    setGridPorts,
    getAllDataResetFilter,
    resetPortList
  }, dispatch);
};

export default connect(
  mapStateToProps,
  mapDispatchToProps,
  )(withStyles(PortSettingsSideDrawerStyles)(PortSettingsSideDrawer));
